/**
 * Patients Management API endpoints
 */
import { api } from './baseApi';

interface PaginatedResponse<T> {
  success: boolean;
  count: number;
  next: string | null;
  previous: string | null;
  total_pages: number;
  current_page: number;
  results: T[];
}

export const LEAD_SOURCES = [
  'FACEBOOK',
  'INSTAGRAM',
  'TIKTOK',
  'GOOGLE',
  'WEBSITE',
  'FRIEND_REFERRAL',
  'DOCTOR_REFERRAL',
  'HOSPITAL',
  'PREVIOUS_CUSTOMER',
  'OTHER',
] as const;

export const CONTACT_METHODS = ['PHONE_CALL', 'WHATSAPP', 'BOTH'] as const;

export type LeadSource = (typeof LEAD_SOURCES)[number];
export type ContactMethod = (typeof CONTACT_METHODS)[number];

export interface Patient {
  id: number;
  user: number | null;
  user_email: string | null;
  user_full_name?: string | null;
  full_name: string;
  date_of_birth?: string | null;
  age: number | null;
  gender: string;
  national_id?: string;
  nationality?: string;
  lead_source?: LeadSource | '';
  lead_source_details?: string;
  previous_home_care_experience?: string;
  preferred_contact_method?: ContactMethod | '';
  preferred_contact_time?: string;
  contact_notes?: string;
  orders_count?: number;
  last_order_at?: string | null;
  phone_number: string;
  email: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  blood_type?: string;
  height?: number | string;
  weight?: number | string;
  bmi?: number | null;
  health_conditions?: string;
  allergies?: string;
  current_medications?: string;
  past_surgeries?: string;
  family_medical_history?: string;
  special_notes?: string;
  medical_reports?: string | null;
  prescriptions?: string | null;
  id_document?: string | null;
  insurance_provider?: string;
  insurance_policy_number?: string;
  insurance_expiry_date?: string | null;
  has_insurance: boolean;
  has_medical_documents: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by?: number | null;
  created_by_email?: string | null;
}

interface PatientResponse {
  success: boolean;
  data: Patient;
}

export const patientsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getPatientsStatistics: builder.query<
      {
        success: boolean;
        data: {
          total_patients: number;
          active_patients: number;
          inactive_patients: number;
          patients_with_user_account: number;
          patients_without_user_account: number;
          patients_with_insurance: number;
          male_patients: number;
          female_patients: number;
          average_age: number;
          patients_created_this_month: number;
          patients_created_this_week: number;
        };
      },
      void
    >({
      query: () => '/dashboard/patients/statistics/',
      providesTags: ['Statistics'],
    }),
    getUserPatients: builder.query<PaginatedResponse<Patient>, { user_id: number; page?: number }>({
      query: ({ user_id, ...params }) => ({
        url: `/dashboard/users/${user_id}/patients/`,
        params,
      }),
      providesTags: ['Users'],
    }),
    getPatients: builder.query<
      PaginatedResponse<Patient>,
      {
        search?: string;
        page?: number;
        page_size?: number;
        is_active?: boolean;
        gender?: 'M' | 'F';
        has_account?: boolean;
        has_insurance?: boolean;
        lead_source?: LeadSource;
      }
    >({
      query: (params) => ({
        url: '/dashboard/patients/',
        params,
      }),
      providesTags: ['Users'],
    }),
    getPatient: builder.query<PatientResponse, number>({
      query: (id) => `/dashboard/patients/${id}/`,
      providesTags: (_result, _error, id) => [{ type: 'User', id }],
    }),
    createPatient: builder.mutation<PatientResponse, Record<string, unknown>>({
      query: (data) => ({
        url: '/dashboard/patients/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Users'],
    }),
    updatePatient: builder.mutation<PatientResponse, { id: number; data: Record<string, unknown> }>({
      query: ({ id, data }) => ({
        url: `/dashboard/patients/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'User', id }, 'Users'],
    }),
    deletePatient: builder.mutation<void, number>({
      query: (id) => ({
        url: `/dashboard/patients/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Users'],
    }),
    linkPatientToUser: builder.mutation<PatientResponse, { id: number; user_id: number }>({
      query: ({ id, user_id }) => ({
        url: `/dashboard/patients/${id}/link-user/`,
        method: 'POST',
        body: { user_id },
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'User', id }, 'Users'],
    }),
    unlinkPatientFromUser: builder.mutation<PatientResponse, number>({
      query: (id) => ({
        url: `/dashboard/patients/${id}/link-user/`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [{ type: 'User', id }, 'Users'],
    }),
  }),
});

export const {
  useGetPatientsStatisticsQuery,
  useGetUserPatientsQuery,
  useGetPatientsQuery,
  useGetPatientQuery,
  useCreatePatientMutation,
  useUpdatePatientMutation,
  useDeletePatientMutation,
  useLinkPatientToUserMutation,
  useUnlinkPatientFromUserMutation,
} = patientsApi;

